import { useState } from 'react';
import ChatMarkdown from './common/ChatMarkdown';
import type { PlannerQuestionCard as Card } from '../types';

/**
 * The planner's interview: a few questions with clickable answers, shown in
 * the chat when it calls `ask_user`.
 *
 * The answers go back as one ordinary chat message (see `formatAnswers`), so
 * the conversation reads naturally afterwards and the planner needs nothing
 * new to understand them. Every question takes typed text too, because the
 * options are the planner's guesses from the research, not the only answers.
 */

interface Props {
  card: Card;
  disabled?: boolean;
  onSubmit: (text: string) => void;
}

/** One chat message: each question, then its answers as a list. Exported for tests. */
export function formatAnswers(
  card: Card,
  picked: string[][],
  typed: string[],
): string {
  return card.questions
    .map((q, i) => {
      const answers = [...(picked[i] ?? [])];
      const own = (typed[i] ?? '').trim();
      if (own) answers.push(own);
      if (!answers.length) return '';
      return `${q.question}\n${answers.map((a) => `- ${a}`).join('\n')}`;
    })
    .filter(Boolean)
    .join('\n\n');
}

export default function PlannerQuestionCard({ card, disabled, onSubmit }: Props) {
  const [picked, setPicked] = useState<string[][]>(() => card.questions.map(() => []));
  const [typed, setTyped] = useState<string[]>(() => card.questions.map(() => ''));

  const toggle = (qi: number, option: string, multiple: boolean) =>
    setPicked((prev) =>
      prev.map((list, i) => {
        if (i !== qi) return list;
        if (list.includes(option)) return list.filter((o) => o !== option);
        return multiple ? [...list, option] : [option];
      }),
    );

  const text = formatAnswers(card, picked, typed);

  return (
    <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3 space-y-3">
      {card.intro && (
        <div className="text-sm text-gray-800 dark:text-gray-200">
          <ChatMarkdown text={card.intro} />
        </div>
      )}
      {card.questions.map((q, qi) => (
        <fieldset key={qi} className="space-y-1.5">
          <legend className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {q.question}
            {q.multiple && (
              <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                (pick any)
              </span>
            )}
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {q.options.map((option) => {
              const on = picked[qi]?.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={on}
                  disabled={disabled}
                  onClick={() => toggle(qi, option, q.multiple)}
                  className={`px-2.5 py-1 text-xs rounded-full border text-left disabled:opacity-50 ${
                    on
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400'
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
          <input
            type="text"
            value={typed[qi] ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setTyped((prev) => prev.map((t, i) => (i === qi ? e.target.value : t)))
            }
            placeholder="Or type your own answer"
            aria-label={`Your own answer: ${q.question}`}
            className="w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100"
          />
        </fieldset>
      ))}
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Skip any question you like. You can change these answers later.
        </span>
        <button
          type="button"
          disabled={disabled || !text}
          onClick={() => onSubmit(text)}
          className="shrink-0 px-3 py-1.5 text-sm font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Send answers
        </button>
      </div>
    </div>
  );
}
